<app-page-header
    [title]="pageTitle"
    [breadcrumbs]="breadcrumbs"
    [buttons]="buttons"
></app-page-header>

<div class="container-fluid">
    <form [formGroup]="manufacturerForm">
        <!-- First Card - Default Price -->
        <mat-card>
            <mat-card-content>
                <div class="row">
                    <div class="col-12 col-md-6">
                        <div class="mb-3">
                            <mat-form-field class="w-100">
                                <mat-label>Manufacturer</mat-label>
                                <mat-select formControlName="manufacturer_id">
                                    @for (manufacturer of manufacturers; track manufacturer) {
                                        <mat-option [value]="manufacturer.id">{{ manufacturer.name }}</mat-option>
                                    }
                                </mat-select>

                                @if (manufacturerForm.controls['manufacturer_id'].invalid) {
                                    <mat-error>Manufacturer is required</mat-error>
                                }
                            </mat-form-field>
                        </div>
                    </div>

                    @if (pricingEnabled && (!isDynamicallyPriced || actionType === 'add')) {
                        <div class="col-12 col-md-6">
                            <div class="mb-3">
                                <mat-form-field class="w-100">
                                    <mat-label>Price effective from</mat-label>
                                    <input matInput [matDatepicker]="picker" [min]="currentDate" formControlName="effective_from" placeholder="Choose a date" />

                                    <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                                    <mat-datepicker #picker></mat-datepicker>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="col-12 my-3 d-flex align-items-center">
                            <h3 class="d-inline-block mb-0">Price</h3>
                            <div class="d-inline-block ms-2" matTooltip="This price is used as the base price when a pricing tier is not found.">
                                <mat-icon>info</mat-icon>
                            </div>
                        </div>

                        <div class="col-12">
                            <div class="row">
                                <div class="col-12 col-md-3">
                                    <div class="mb-3">
                                        <mat-form-field class="w-100">
                                            <mat-label>Operative Cost (£)</mat-label>
                                            <input matInput type="number" min="0" formControlName="operative_cost" />

                                            @if (manufacturerForm.controls['operative_cost'].invalid) {
                                                <mat-error> Operative Cost is required </mat-error>
                                            }
                                        </mat-form-field>
                                    </div>
                                </div>

                                <div class="col-12 col-md-3">
                                    <div class="mb-3">
                                        <mat-form-field class="w-100">
                                            <mat-label>Sub Contractor Cost (£)</mat-label>
                                            <input matInput type="number" min="0" formControlName="subcontractor_cost" />

                                            @if (manufacturerForm.controls['subcontractor_cost'].invalid) {
                                                <mat-error>Sub Contractor Cost is required</mat-error>
                                            }
                                        </mat-form-field>
                                    </div>
                                </div>

                                <div class="col-12 col-md-3">
                                    <div class="mb-3">
                                        <mat-form-field class="w-100">
                                            <mat-label>Material Cost (£)</mat-label>
                                            <input matInput type="number" min="0" formControlName="material_cost" />

                                            @if (manufacturerForm.controls['material_cost'].invalid) {
                                                <mat-error>Material Cost is required</mat-error>
                                            }
                                        </mat-form-field>
                                    </div>
                                </div>

                                <div class="col-12 col-md-3 mb-2">
                                    <div class="mb-3">
                                        <mat-form-field class="w-100">
                                            <mat-label>Sell (£)</mat-label>
                                            <input matInput type="number" min="0" formControlName="sell_price" />

                                            @if (manufacturerForm.controls['sell_price'].invalid) {
                                                <mat-error>Sell Price is required</mat-error>
                                            }
                                        </mat-form-field>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    @if (!pricingEnabled) {
                        <div class="col-12">
                            <p class="mb-0 text-muted">Pricing is disabled for this organisation. You can still link manufacturers to this product.</p>
                        </div>
                    }
                </div>
            </mat-card-content>
        </mat-card>

        <mat-card class="mt-4">
            <mat-card-content>
                <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                    <div>
                        <h3 class="mb-1">Specification Sheets</h3>
                        @if (actionType === 'add') {
                            <p class="mb-0 text-muted">Save the manufacturer link first, then upload specification sheets here.</p>
                        }
                    </div>

                    @if (actionType === 'edit' && hasHiddenDefaultSpecificationSheets) {
                        <button type="button" mat-stroked-button color="primary" (click)="restoreDefaultSpecificationSheets()" [disabled]="specificationSheetRestoring">{{ specificationSheetRestoring ? 'Restoring...' : 'Restore Default Files' }}</button>
                    }
                </div>

                @if (actionType === 'edit') {
                    <div class="manufacturer-specification-sheet-upload-row mt-3">
                        <input #specificationSheetInput type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple (change)="setSpecificationSheetFiles($event)" hidden />
                        <button type="button" mat-stroked-button color="primary" (click)="specificationSheetInput.value = ''; specificationSheetInput.click()">Choose Files</button>
                        <button type="button" mat-flat-button color="primary" (click)="uploadSpecificationSheets(specificationSheetInput)" [disabled]="!specificationSheetFiles.length || specificationSheetUploading">{{ specificationSheetUploading ? 'Uploading...' : 'Upload Files' }}</button>
                    </div>

                    @if (specificationSheetFiles.length) {
                        <div class="selected-specification-sheet-files mt-3">
                            <p class="mb-2"><strong>Selected Files</strong></p>
                            <div class="selected-specification-sheet-chip-list">
                                @for (file of specificationSheetFiles; track file.name + '-' + $index) {
                                    <span class="selected-specification-sheet-chip">{{ file.name }}</span>
                                }
                            </div>
                            <small class="text-muted d-block mt-2">{{ getSelectedSpecificationSheetFileNames() }}</small>
                        </div>
                    }

                    @if (hasSpecificationSheets) {
                        <div class="specification-sheet-list mt-3">
                            @for (specificationSheet of specificationSheets; track specificationSheet.id) {
                                <div class="specification-sheet-card">
                                    <button type="button" mat-icon-button class="specification-sheet-remove-button" (click)="removeSpecificationSheet(specificationSheet)" [attr.aria-label]="'Remove ' + specificationSheet.file_name">
                                        <mat-icon>close</mat-icon>
                                    </button>

                                    <div class="specification-sheet-details">
                                        <div class="specification-sheet-preview specification-sheet-preview-inline">
                                            <div class="specification-sheet-preview-placeholder">
                                                <div class="specification-sheet-preview-document">
                                                    <mat-icon class="specification-sheet-preview-icon" aria-hidden="true">{{ getSpecificationSheetPreviewIcon(specificationSheet) }}</mat-icon>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="specification-sheet-meta">
                                            <strong class="specification-sheet-name">{{ specificationSheet.file_name }}</strong>
                                            <div class="specification-sheet-actions">
                                                <button type="button" mat-stroked-button color="primary" (click)="downloadSpecificationSheet(specificationSheet)">Download</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    } @else {
                        <p class="mb-0 mt-3">No files uploaded for this manufacturer link yet.</p>
                    }
                }
            </mat-card-content>
        </mat-card>

        <!-- Second Card - Pricing Tiers -->
        @if (pricingEnabled && isDynamicallyPriced && actionType === 'edit') {
            <mat-card class="mt-4">
                <mat-card-content>
                    <div class="d-flex align-items-center mb-3">
                        <h3 class="mb-0">Pricing Tiers</h3>
                        <div class="d-inline-block ms-2" matTooltip="Pricing tiers are used when you want to change your pricing based on sizing, using this for Ablative Batt is an example of where this can be useful.">
                            <mat-icon>info</mat-icon>
                        </div>

                        <div class="ms-auto">
                            <button type="button" mat-stroked-button color="primary" class="mx-2" (click)="openAdditionalPriceModal()">View Upcoming Price Changes</button>
                            <button type="button" mat-stroked-button color="primary" class="mx-2" (click)="downloadTemplate()">Download Price Template</button>
                            <button type="button" mat-stroked-button color="primary" class="mx-2" (click)="uploadPrices()">Import Tiers</button>
                            <button type="button" mat-raised-button color="primary" (click)="addPriceTier()">Add Tier</button>
                        </div>
                    </div>

                    <div class="col-12 mb-5 table-responsive">
                        <table mat-table [dataSource]="dataSource" class="w-100">
                            <ng-container matColumnDef="tier">
                                <th mat-header-cell *matHeaderCellDef>Tier</th>
                                <td mat-cell *matCellDef="let row">{{ row.tier }}</td>
                            </ng-container>

                            <ng-container matColumnDef="operative_cost">
                                <th mat-header-cell *matHeaderCellDef>Operative Cost (£)</th>
                                <td mat-cell *matCellDef="let row">{{ row.operative_cost | currency: "GBP":"symbol":"1.2-2" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="subcontractor_cost">
                                <th mat-header-cell *matHeaderCellDef>Sub Contractor Cost (£)</th>
                                <td mat-cell *matCellDef="let row">{{ row.subcontractor_cost | currency: "GBP":"symbol":"1.2-2" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="material_cost">
                                <th mat-header-cell *matHeaderCellDef>Material Cost (£)</th>
                                <td mat-cell *matCellDef="let row">{{ row.material_cost | currency: "GBP":"symbol":"1.2-2" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="sell_price">
                                <th mat-header-cell *matHeaderCellDef>Sell (£)</th>
                                <td mat-cell *matCellDef="let row">{{ row.sell_price | currency: "GBP":"symbol":"1.2-2" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="effective_from">
                                <th mat-header-cell *matHeaderCellDef>Effective From</th>
                                <td mat-cell *matCellDef="let row">{{ row.effective_from | date: "dd/MM/yyyy" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="effective_to">
                                <th mat-header-cell *matHeaderCellDef>Effective To</th>
                                <td mat-cell *matCellDef="let row">{{ row.effective_to | date: "dd/MM/yyyy" }}</td>
                            </ng-container>

                            <ng-container matColumnDef="action">
                                <th mat-header-cell *matHeaderCellDef>Action</th>
                                <td mat-cell *matCellDef="let row">
                                    <a mat-button class="hide-on-mobile" (click)="editPriceTier(row)">Edit</a>
                                    <button mat-icon-button (click)="$event.stopPropagation()" [matMenuTriggerFor]="menu"><mat-icon>more_vert</mat-icon></button>

                                    <mat-menu #menu="matMenu">
                                        <button mat-menu-item class="hide-on-desktop" (click)="editPriceTier(row)">Edit</button>

                                        @if (row.tier > 0) {
                                            <mat-divider></mat-divider>
                                            <a mat-menu-item (click)="confirmArchivePriceTier(row)">Archive</a>
                                        }
                                    </mat-menu>
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="displayColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: displayColumns" [ngClass]="{ 'highlight-bg': row.edited }"></tr>
                            <!-- No data message -->
                            <tr class="mat-row" *matNoDataRow>
                                <td class="mat-cell text-center" [attr.colspan]="displayColumns.length">
                                    No active pricing tiers found
                                </td>
                            </tr>
                        </table>

                        <mat-paginator
                            [length]="totalRows"
                            [pageIndex]="currentPage"
                            [pageSize]="pageSize"
                            [pageSizeOptions]="pageSizeOptions"
                            (page)="pageChanged($event)"
                        >
                        </mat-paginator>
                    </div>
                </mat-card-content>
            </mat-card>
        }
    </form>
</div>
